import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceMetrics = ({
  score = 85,
  totalQuestions = 10,
  correctAnswers = 8,
  timeSpent = 420,
  passingScore = 70,
  performanceLevel = "Good"
}) => {
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getPerformanceBadge = () => {
    if (score >= 90) return { label: "Excellent", color: "success", icon: "Trophy" };
    if (score >= 70) return { label: "Good", color: "secondary", icon: "CheckCircle2" };
    return { label: "Needs Improvement", color: "warning", icon: "AlertCircle" };
  };

  const badge = getPerformanceBadge();
  const efficiency = Math.round((correctAnswers / totalQuestions) * (600 / timeSpent) * 100);

  return (
    <div className="bg-surface rounded-xl border border-border p-6 card-shadow mb-6">
      <div className="text-center space-y-6">
        {/* Main Score Display */}
        <div className="flex flex-col items-center space-y-4">
          <div className={`
            flex items-center justify-center w-24 h-24 rounded-full
            ${badge.color === 'success' ? 'bg-success-50' :
              badge.color === 'secondary' ? 'bg-secondary-50' : 'bg-warning-50'}
          `}>
            <Icon 
              name={badge.icon} 
              size={36} 
              color={`var(--color-${badge.color})`}
              strokeWidth={2}
            />
          </div>
          
          <div>
            <div className="text-5xl font-heading font-bold text-text-primary mb-2">
              {score}%
            </div>
            <div className="text-lg font-medium text-text-secondary">
              {correctAnswers} of {totalQuestions} questions correct
            </div>
          </div>
        </div>

        {/* Performance Badge */}
        <div className={`
          inline-flex items-center space-x-2 px-6 py-3 rounded-full
          ${badge.color === 'success' ? 'bg-success-50 text-success-700' :
            badge.color === 'secondary'? 'bg-secondary-50 text-secondary-700' : 'bg-warning-50 text-warning-700'}
        `}>
          <Icon 
            name={badge.icon} 
            size={18} 
            color="currentColor"
            strokeWidth={2}
          />
          <span className="font-semibold text-lg">
            {badge.label}
          </span>
        </div>

        {/* Performance Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
          <div className="bg-surface-secondary rounded-lg p-4 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-lg mx-auto mb-3">
              <Icon name="Target" size={24} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div className="text-2xl font-heading font-bold text-text-primary mb-1">
              {score}%
            </div>
            <div className="text-sm text-text-secondary">Accuracy Score</div>
          </div>

          <div className="bg-surface-secondary rounded-lg p-4 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-accent-50 rounded-lg mx-auto mb-3">
              <Icon name="Clock" size={24} color="var(--color-accent)" strokeWidth={2} />
            </div>
            <div className="text-2xl font-heading font-bold text-text-primary mb-1">
              {formatTime(timeSpent)}
            </div>
            <div className="text-sm text-text-secondary">Time Spent</div>
          </div>

          <div className="bg-surface-secondary rounded-lg p-4 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-secondary-50 rounded-lg mx-auto mb-3">
              <Icon name="Zap" size={24} color="var(--color-secondary)" strokeWidth={2} />
            </div>
            <div className="text-2xl font-heading font-bold text-text-primary mb-1">
              {efficiency}
            </div>
            <div className="text-sm text-text-secondary">Efficiency Score</div>
          </div>
        </div>

        {/* Pass/Fail Status */}
        <div className={`
          inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium
          ${score >= passingScore 
            ? 'bg-success-100 text-success-800' :'bg-error-100 text-error-800'}
        `}>
          <Icon 
            name={score >= passingScore ? "CheckCircle2" : "XCircle"} 
            size={16} 
            color="currentColor"
            strokeWidth={2}
          />
          <span>
            {score >= passingScore ? 'Passed' : 'Failed'} • {passingScore}% required to pass
          </span>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;